/**
 * Seed multi-modal case for the /fusion demo — one suspect ("R. Kumar") across
 * banking, social, company and crypto, joined by an identity hub. Organised as
 * the output of per-module ADAPTERS (bank/crypto/social/company) and tagged with
 * the ingestion STEP at which each element arrives, so the canvas can replay the
 * investigation workflow ("data arriving into one place").
 *
 * Demo data — not live. Production feeds the SAME shape from the real
 * to_envelope() adapters (GraphService, fiat_graph, OSINT, ROC); swapping the
 * arrays below for fetched data is the only change. See MULTIMODAL_ENVELOPE_CONTRACT.md §8.
 */
import type { FusionNode, FusionEdge } from '../types/fusion';
import type { ElementDefinition } from 'cytoscape';

const A = (method: string, tier: 0 | 1 | 2 | 3, confidence: number, source: string, evidence_ref?: string) =>
  ({ method, tier, confidence, source, evidence_ref });

/** The investigation workflow — each stage is one surface arriving. */
export interface IngestStep { n: number; title: string; sub: string; module: string; }
export const INGEST_STEPS: IngestStep[] = [
  { n: 1, title: 'Bank statement', sub: 'Account + UPI/IMPS legs from the statement', module: 'bank' },
  { n: 2, title: 'UPI → crypto handover', sub: 'Off-ramp wallet joined on UTR / VPA', module: 'crypto' },
  { n: 3, title: 'On-chain trace', sub: 'USDT-TRC20 path to exchange deposit', module: 'crypto' },
  { n: 4, title: 'OSINT', sub: 'Handles and ads that advertise the same VPA', module: 'social' },
  { n: 5, title: 'Identity hub', sub: 'Company registry + one suspect across surfaces', module: 'company' },
];
export const MAX_STEP = INGEST_STEPS.length;

/** Stages for the USER's OWN data (shown when the demo seed is hidden). Grouped
 *  by surface/type so the play button reveals it one layer at a time:
 *  identifiers you typed → the bank account → its counterparties. */
export const USER_STAGE = { IDENTIFIERS: 1, ACCOUNT: 2, COUNTERPARTIES: 3 } as const;
export const USER_INGEST_STEPS: IngestStep[] = [
  { n: 1, title: 'Identifiers added', sub: 'Phone / UPI / handle / name you entered', module: 'shared' },
  { n: 2, title: 'Bank account', sub: 'Account parsed from the uploaded statement', module: 'bank' },
  { n: 3, title: 'Counterparties', sub: 'Parties the statement paid / received from', module: 'bank' },
];
export const USER_MAX_STEP = USER_INGEST_STEPS.length;

const tx = (id: string, ref: string, direction: 'in' | 'out', counterparty: string, value: number, unit: string, ts: string, kind: string): import('../types/fusion').TxDetail =>
  ({ id, ref, direction, counterparty, value, unit, ts, kind });

export const SEED_NODES: FusionNode[] = [
  // ── Step 5: identity hub (consolidates everything, arrives last) ──
  { id: 'id:rkumar', module: 'shared', type: 'identity_hub', label: 'Suspect: R. Kumar', risk_score: 0.92,
    placement_role: 'shared_relay', size: 11, step: 5, attribution: A('case_identity', 1, 0.95, 'NCRP complaint #4471 + KYC'),
    module_extras: { aliases: ['Rajesh Kumar', '@kumar_otc', '@kumarp2p'], case_id: 'ZZZ-DEMO', surfaces: 4 } },

  // ── Step 1: Banking (StatementFlow) ──
  { id: 'account:50100', module: 'bank', type: 'account', label: 'HDFC ••5100', risk_score: 0.74, size: 8, step: 1,
    attribution: A('bank_notice_return', 1, 0.9, 'HDFC §91 return', 'notice/HDFC-2291'),
    module_extras: { ifsc: 'HDFC0001234', acct_type: 'SB', dr_total: 1820000, cr_total: 2010000, mule_score: 0.81 },
    txns: [
      tx('b1', 'UTR 9000124468', 'in', 'victim VPA · ramesh@oksbi', 95000, 'INR', '2026-06-18T10:22:00', 'UPI'),
      tx('b2', 'UTR 9000124471', 'out', 'otc VPA · kumar.otc@okhdfc', 480000, 'INR', '2026-06-18T11:05:00', 'UPI'),
      tx('b3', 'UTR 9000119922', 'in', 'victim VPA · sita@okicici', 120000, 'INR', '2026-06-17T19:40:00', 'UPI'),
      tx('b4', 'IMPS 5120••8841', 'out', 'cash withdrawal ATM', 49000, 'INR', '2026-06-18T13:10:00', 'IMPS'),
      tx('b5', 'UTR 9000131007', 'in', 'victim VPA · anil@okaxis', 78000, 'INR', '2026-06-19T09:02:00', 'UPI'),
    ] },
  { id: 'person:rkumar', module: 'bank', type: 'person', label: 'Rajesh Kumar (KYC)', risk_score: 0.6, size: 5, step: 1,
    semantic_role: 'suspect_hub',
    attribution: A('bank_kyc', 1, 0.9, 'HDFC KYC'), module_extras: { pan: 'ABCPK••••K', phone: '+91-98••••12' } },

  // ── Step 2: the off-ramp wallet (FlowGuard) ──
  // Group container (presentation only): no evidence attribution, styled as a compound by the stylesheet.
  { id: 'box:crypto', module: 'crypto', type: 'cluster', label: 'On-Chain Layering', risk_score: 0.5, step: 2 },
  { id: 'TRX:TRc9k6', cluster_id: 'box:crypto', module: 'crypto', type: 'wallet', label: 'TRc9k6…1e1U', risk_score: 0.82, size: 9, step: 2,
    semantic_role: 'p2p_bridge',
    attribution: A('registry_cluster', 2, 0.8, 'VASP/OTC registry'),
    module_extras: { chain: 'TRX', usdt_balance: 0, tx_count: 412, off_ramp: 'Binance' },
    txns: [
      tx('c1', '221441191d0397…', 'in', 'account ••5100 (via OTC)', 5600, 'USDT', '2026-06-18T11:40:00', 'TRC20'),
      tx('c2', 'ef8ba873c48bb9…', 'out', 'TRkocK… (Binance dep)', 5200, 'USDT', '2026-06-18T12:18:00', 'TRC20'),
      tx('c3', '348ea077d5e3cd…', 'out', 'TXhQuS… (collection)', 3100, 'USDT', '2026-06-18T12:25:00', 'TRC20'),
      tx('c4', '2c5da57d620912…', 'in', 'TVff5h… (peer)', 2200, 'USDT', '2026-06-17T20:10:00', 'TRC20'),
      tx('c5', '86407930ae2883…', 'out', 'TYAjKM… (collection)', 1800, 'USDT', '2026-06-18T12:31:00', 'TRC20'),
    ] },

  // ── Step 3: on-chain destinations (CryptoFlow) ──
  { id: 'TRX:binance_dep', cluster_id: 'box:crypto', module: 'crypto', type: 'exchange', label: 'Binance deposit', risk_score: 0.3, size: 7, step: 3,
    attribution: A('dune_cex_tron', 1, 0.9, 'Dune CEX-deposit set'),
    module_extras: { chain: 'TRX', exchange: 'Binance', wallet_role: 'deposit', subpoena: 'BIN-8842' } },
  { id: 'cluster:deposits', cluster_id: 'box:crypto', module: 'crypto', type: 'cluster', label: '12 collection wallets', risk_score: 0.7, size: 6, step: 3,
    attribution: A('co_spend_cluster', 2, 0.75, 'clustering engine'), module_extras: { members: 12, total_usdt: 38000 } },

  // ── Step 4: OSINT (Telegram / Instagram + device) ──
  // Group container (presentation only): no evidence attribution.
  { id: 'box:osint', module: 'social', type: 'cluster', label: 'OSINT Footprint', risk_score: 0.5, step: 4 },
  { id: 'ig:@kumar_otc', cluster_id: 'box:osint', module: 'social', type: 'handle', label: '@kumar_otc (IG)', risk_score: 0.55, size: 4, step: 4,
    attribution: A('osint', 3, 0.78, 'Instagram OSINT (Tier-4)'), module_extras: { followers: 1840, bio_has_upi: true },
    txns: [
      tx('s1', 'post/Cx91', 'out', 'public post', 0, '', '2026-06-15T08:00:00', 'post · "USDT@best rate, UPI kumar.otc@okhdfc"'),
      tx('s2', 'story/CY12', 'out', 'public story', 0, '', '2026-06-16T18:30:00', 'post · QR (decodes to kumar.otc@okhdfc)'),
    ] },
  { id: 'tg:@kumarp2p', cluster_id: 'box:osint', module: 'social', type: 'handle', label: '@kumarp2p (TG)', risk_score: 0.5, size: 4, step: 4,
    attribution: A('osint', 3, 0.6, 'Telegram OTC intel'), module_extras: { channel: 'INR-USDT-OTC' } },
  { id: 'ip:103.21', cluster_id: 'box:osint', module: 'network', type: 'ip', label: '103.21.• (login)', risk_score: 0.32, size: 3, step: 4,
    attribution: A('device_observation', 3, 0.6, 'bank login telemetry') },

  // ── Step 5: Company (ROC/MCA21) ──
  { id: 'cin:U74999', module: 'company', type: 'company', label: 'Kumar Trading Pvt Ltd', risk_score: 0.42, size: 6, step: 5,
    attribution: A('roc_registry', 2, 0.95, 'MCA21 ROC', 'cin/U74999DL2023'), module_extras: { gstin: '07AAB••••1Z5', charges: 1, status: 'Active' } },
  { id: 'din:07', module: 'company', type: 'director', label: 'R. Kumar (DIN 07••)', risk_score: 0.5, size: 4, step: 5,
    attribution: A('roc_registry', 2, 0.92, 'MCA21 DIN') },

  // ── Analyst note (demo annotation; written by hand, not by a model) ──
  { id: 'anno:1', module: 'shared', type: 'annotation', label: 'Analyst note (demo): funds consolidated across 12 collection wallets within 24h. Review as a possible layering pattern.', risk_score: 0, step: 3, attribution: A('analyst_note', 3, 0.5, 'Demo annotation (synthetic)') }
];

export const SEED_EDGES: FusionEdge[] = [
  // Step 1 — banking
  { id: 'e5', source: 'person:rkumar', target: 'account:50100', edge_type: 'ownership', determinism: 'deterministic', step: 1,
    join_key: 'account holder (KYC)', attribution: A('bank_kyc', 1, 0.95, 'HDFC') },

  // Step 2 — the bank→crypto handover (the headline)
  { id: 'e8', source: 'account:50100', target: 'TRX:TRc9k6', edge_type: 'p2p', determinism: 'deterministic', step: 2,
    value: 480000, unit: 'INR', count: 6, join_key: 'VPA→wallet (registry) + UTR 9000124471',
    attribution: A('upi_registry_join', 2, 0.88, 'VASP/OTC VPA registry') },

  // Step 3 — on-chain
  { id: 'e9', source: 'TRX:TRc9k6', target: 'TRX:binance_dep', edge_type: 'trace', determinism: 'deterministic', step: 3,
    value: 5200, unit: 'USDT', count: 3, join_key: 'tx_hash 221441…0397', attribution: A('on_chain', 0, 0.99, 'TRON ledger') },
  { id: 'e10', source: 'TRX:TRc9k6', target: 'cluster:deposits', edge_type: 'trace', determinism: 'deterministic', step: 3,
    value: 38000, unit: 'USDT', count: 41, join_key: 'on-chain batch', attribution: A('on_chain', 0, 0.99, 'TRON ledger') },
  { id: 'e11', source: 'TRX:binance_dep', target: 'person:rkumar', edge_type: 'proof_anchor', determinism: 'deterministic', step: 3,
    join_key: 'Binance §91 KYC return', attribution: A('exchange_notice_return', 1, 0.97, 'Binance §91', 'notice/BIN-8842') },

  // Step 4 — OSINT
  { id: 'e7', source: 'person:rkumar', target: 'ig:@kumar_otc', edge_type: 'affiliation', determinism: 'scored', step: 4,
    join_key: 'handle attribution', attribution: A('osint', 3, 0.6, 'OSINT') },
  { id: 'e12', source: 'ig:@kumar_otc', target: 'TRX:TRc9k6', edge_type: 'cross_link', determinism: 'scored', step: 4,
    join_key: 'handle advertised this VPA→wallet', attribution: A('osint_post', 3, 0.65, 'IG bio + QR scrape') },
  { id: 'e13', source: 'ip:103.21', target: 'account:50100', edge_type: 'cross_link', determinism: 'scored', step: 4,
    join_key: 'login-IP correlation', attribution: A('device_corr', 3, 0.6, 'login telemetry') },
  { id: 'e14', source: 'tg:@kumarp2p', target: 'ig:@kumar_otc', edge_type: 'affiliation', determinism: 'scored', step: 4,
    join_key: 'cross-platform handle', attribution: A('osint', 3, 0.55, 'OTC intel') },

  // Step 5 — company + the identity-hub consolidation
  { id: 'e6', source: 'din:07', target: 'cin:U74999', edge_type: 'ownership', determinism: 'deterministic', step: 5,
    join_key: 'director_of', attribution: A('roc_registry', 2, 0.95, 'MCA21') },
  { id: 'e1', source: 'id:rkumar', target: 'account:50100', edge_type: 'cross_link', determinism: 'deterministic', step: 5,
    join_key: 'KYC / PAN match', attribution: A('kyc_match', 1, 0.95, 'HDFC KYC') },
  { id: 'e2', source: 'id:rkumar', target: 'cin:U74999', edge_type: 'cross_link', determinism: 'deterministic', step: 5,
    join_key: 'DIN ↔ PAN', attribution: A('mca_pan', 2, 0.9, 'MCA21') },
  { id: 'e15', source: 'id:rkumar', target: 'TRX:TRc9k6', edge_type: 'cross_link', determinism: 'scored', step: 5,
    join_key: 'fiat↔crypto correlation', attribution: A('fiat_crypto_corr', 3, 0.71, 'FiatBridge L4') },
  { id: 'e16', source: 'id:rkumar', target: 'ig:@kumar_otc', edge_type: 'cross_link', determinism: 'scored', step: 5,
    join_key: 'OSINT correlation', attribution: A('osint_corr', 3, 0.78, 'name+phone overlap') },
  { id: 'e_anno1', source: 'anno:1', target: 'cluster:deposits', edge_type: 'annotation_link', determinism: 'scored', step: 3,
    attribution: A('analyst_note', 3, 0.5, 'Demo annotation (synthetic)') }
];

const SHORT = (s: string, n = 26) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

function nodeEl(n: FusionNode): ElementDefinition {
  // Intent only (R23): the typed semantic_role drives the stylesheet; no CSS
  // class names travel in data.
  return {
    data: {
      id: n.id, label: n.label, module: n.module, type: n.type, risk_score: n.risk_score ?? 0, size: n.size ?? 1, step: n.step ?? 1, placement_role: n.placement_role || '',
      ...(n.semantic_role ? { semantic_role: n.semantic_role } : {}),
      parent: n.cluster_id || undefined,
    },
  };
}
function edgeEl(e: FusionEdge): ElementDefinition {
  return { data: {
    id: e.id, source: e.source, target: e.target, edge_type: e.edge_type, determinism: e.determinism, value: e.value ?? 0, step: e.step ?? 1,
    _edgeLabel: e.determinism === 'scored' ? `~${e.attribution.confidence.toFixed(2)}` : SHORT(e.join_key || ''),
  } };
}

/** All elements (used for full render). */
export function toCyElements(nodes = SEED_NODES, edges = SEED_EDGES): ElementDefinition[] {
  return [...nodes.map(nodeEl), ...edges.map(edgeEl)];
}

/** Elements that have arrived by `step` (used for the ingestion replay). */
export function elementsUpToStep(step: number): ElementDefinition[] {
  const nodes = SEED_NODES.filter((n) => (n.step ?? 1) <= step).map(nodeEl);
  const edges = SEED_EDGES.filter((e) => (e.step ?? 1) <= step
    && SEED_NODES.some((n) => n.id === e.source && (n.step ?? 1) <= step)
    && SEED_NODES.some((n) => n.id === e.target && (n.step ?? 1) <= step)).map(edgeEl);
  return [...nodes, ...edges];
}

export const nodeById = (id: string) => SEED_NODES.find((n) => n.id === id);
export const edgeById = (id: string) => SEED_EDGES.find((e) => e.id === id);
